import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameStore, useGameData } from '../hooks/useGame';
import Button from '../components/ui/Button';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { motion } from 'framer-motion';
import { Star, Settings, Zap, Award, Volume2, Mic, Send, X } from 'lucide-react';

const GameScreenWrapper = ({ children }: { children: React.ReactNode }) => (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-100 flex items-center justify-center p-4 font-sans">
        <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.5, type: 'spring' }}
            className="w-full max-w-2xl"
        >
            {children}
        </motion.div>
    </div>
);

const GameCard = ({ children, className }: { children: React.ReactNode, className?: string }) => (
    <div className={`bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-6 sm:p-8 border-4 border-white/50 ${className}`}>
        {children}
    </div>
);

const GameTitle = ({ icon, title, subtitle }: { icon: string, title: string, subtitle: string }) => (
    <div className="text-center mb-8">
        <div className="text-7xl mb-4">{icon}</div>
        <h1 className="text-4xl sm:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600 mb-2">
            {title}
        </h1>
        <p className="text-lg text-gray-600">{subtitle}</p>
    </div>
);

const DifficultySelector = ({ value, onChange }: { value: number, onChange: (value: number) => void }) => (
    <div>
        <label className="block text-lg font-bold text-gray-800 mb-3 flex items-center">
            <Zap className="w-6 h-6 mr-2 text-yellow-500" />
            Choose Your Challenge
        </label>
        <div className="grid grid-cols-3 gap-3">
            {[
                { value: 1, label: 'Easy', icon: '🌱' },
                { value: 2, label: 'Medium', icon: '🚀' },
                { value: 3, label: 'Hard', icon: '🏆' },
            ].map((diff) => (
                <motion.button
                    key={diff.value}
                    onClick={() => onChange(diff.value)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`p-4 rounded-2xl text-white font-bold text-lg transition-all shadow-lg ${
                        value === diff.value
                            ? 'bg-gradient-to-r from-green-400 to-blue-500 ring-4 ring-yellow-300'
                            : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                >
                    <span className="text-2xl">{diff.icon}</span>
                    <div>{diff.label}</div>
                </motion.button>
            ))}
        </div>
    </div>
);

const CategorySelector = ({ value, onChange, categories, isLoading }: { value: string, onChange: (value: string) => void, categories: any[], isLoading: boolean }) => (
    <div>
        <label className="block text-lg font-bold text-gray-800 mb-3 flex items-center">
            <Settings className="w-6 h-6 mr-2 text-purple-500" />
            Select a Topic
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-64 overflow-y-auto p-2 bg-gray-100 rounded-2xl">
            {isLoading ? (
                <div className="col-span-full text-center py-8 text-gray-500">
                    <LoadingSpinner />
                    <p>Loading Topics...</p>
                </div>
            ) : (
                categories.map((cat) => (
                    <motion.button
                        key={cat.id}
                        onClick={() => onChange(cat.name)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className={`p-4 rounded-2xl font-bold text-sm transition-all shadow-md ${
                            value === cat.name
                                ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white ring-4 ring-pink-300'
                                : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-yellow-300'
                        }`}
                    >
                        <div className="text-3xl mb-1">{cat.icon || '📚'}</div>
                        <div className="font-bold">{cat.display_name}</div>
                        <div className="text-xs opacity-75 mt-1">{cat.word_count} words</div>
                    </motion.button>
                ))
            )}
        </div>
    </div>
);

const GameSetup = ({ onStart }: { onStart: (difficulty: number, category: string) => void }) => {
    const [difficulty, setDifficulty] = useState(1);
    const [category, setCategory] = useState('');
    const { categories, categoriesLoading } = useGameData();

    useEffect(() => {
        if (categories && Array.isArray(categories) && categories.length > 0 && !category) {
            setCategory((categories as any)[0].name);
        }
    }, [categories, category]);

    return (
        <GameScreenWrapper>
            <GameCard>
                <GameTitle icon="🎮" title="Game Time!" subtitle="Get ready for a fun challenge" />
                <div className="space-y-6 mb-8">
                    <DifficultySelector value={difficulty} onChange={setDifficulty} />
                    <CategorySelector value={category} onChange={setCategory} categories={Array.isArray(categories) ? categories : []} isLoading={categoriesLoading} />
                </div>
                <Button
                    onClick={() => onStart(difficulty, category)}
                    className="w-full h-16 text-xl font-bold bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 shadow-xl"
                    disabled={!category || categoriesLoading}
                >
                    Let's Play!
                </Button>
            </GameCard>
        </GameScreenWrapper>
    );
};

const GameEnded = ({ score, streak }: { score: number, streak: number }) => {
    const { reset } = useGameStore();
    const navigate = useNavigate();

    return (
        <GameScreenWrapper>
            <GameCard>
                <GameTitle icon="🎉" title="Awesome Job!" subtitle="You're a pronunciation superstar!" />
                <div className="space-y-6 mb-8">
                    <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl p-6 grid grid-cols-2 gap-4">
                        <div className="text-center">
                            <div className="text-5xl font-bold text-purple-600 mb-2">{score}</div>
                            <div className="text-lg text-gray-700 font-medium flex items-center justify-center"><Award className="w-5 h-5 mr-2"/> Final Score</div>
                        </div>
                        <div className="text-center">
                            <div className="text-5xl font-bold text-blue-600 mb-2">{streak}</div>
                            <div className="text-lg text-gray-700 font-medium flex items-center justify-center"><Star className="w-5 h-5 mr-2"/> Best Streak</div>
                        </div>
                    </div>
                </div>
                <Button
                    onClick={() => { reset(); navigate('/dashboard'); }}
                    className="w-full h-16 text-xl font-bold bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 shadow-xl"
                >
                    Play Again
                </Button>
            </GameCard>
        </GameScreenWrapper>
    );
};

const WordDisplay = ({ word }: { word: any }) => (
    <div className="text-center mb-8 bg-gradient-to-r from-yellow-100 to-orange-100 rounded-3xl p-6">
        <h2 className="text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 mb-3">
            {word.word}
        </h2>
        <p className="text-xl text-gray-600 font-medium flex items-center justify-center">
            <Volume2 className="w-5 h-5 mr-2" /> /{word.phonetic_transcription}/
        </p>
    </div>
);

const RecordButton = ({ isRecording, onToggle }: { isRecording: boolean, onToggle: () => void }) => (
    <div className="text-center">
        <p className="text-lg text-gray-700 mb-4 font-medium">
            {isRecording ? "Recording... Speak now!" : "Tap the mic to record"}
        </p>
        <motion.button
            onClick={onToggle}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className={`w-48 h-48 rounded-full text-white font-bold text-2xl shadow-2xl transition-all flex flex-col items-center justify-center ring-8 ${isRecording ? 'bg-red-500 ring-red-200 animate-pulse' : 'bg-green-500 ring-green-200'}`}>
            <Mic size={64} />
            <span>{isRecording ? 'Stop' : 'Record'}</span>
        </motion.button>
    </div>
);

const FeedbackDisplay = ({ feedback }: { feedback: any }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full bg-gradient-to-r from-green-100 to-blue-100 rounded-3xl p-6 border-4 border-green-200 mb-8"
    >
        <div className="text-center mb-4">
            <div className="text-4xl mb-2">🎉</div>
            <h3 className="text-2xl font-bold text-gray-900">Great Try!</h3>
        </div>
        <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="text-center bg-white rounded-2xl p-4 shadow-md">
                <div className="text-4xl font-bold text-purple-600">{feedback.overall_score}%</div>
                <div className="text-sm font-medium text-gray-600">Overall</div>
            </div>
            <div className="text-center bg-white rounded-2xl p-4 shadow-md">
                <div className="text-4xl font-bold text-blue-600">{feedback.accuracy}%</div>
                <div className="text-sm font-medium text-gray-600">Accuracy</div>
            </div>
            <div className="text-center bg-white rounded-2xl p-4 shadow-md">
                <div className="text-4xl font-bold text-green-600">{feedback.fluency}%</div>
                <div className="text-sm font-medium text-gray-600">Fluency</div>
            </div>
        </div>
        {feedback.feedback_text && (
            <div className="bg-white rounded-2xl p-4 shadow-md text-center text-gray-700">
                {feedback.feedback_text}
            </div>
        )}
    </motion.div>
);

export default function GamePage() {
    const {
        gameState,
        recordingState,
        feedback,
        startGame,
        endGame,
        setCurrentWord,
        startRecording,
        stopRecording,
        processAudio,
        clearFeedback,
    } = useGameStore();

    const { fetchWords } = useGameData();
    const [availableWords, setAvailableWords] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const handleStartGame = async (difficulty: number, category: string) => {
        setIsLoading(true);
        const words = await fetchWords(category, difficulty);
        setAvailableWords(words);
        if (words.length > 0) {
            const randomWord = words[Math.floor(Math.random() * words.length)];
            startGame('word_practice', difficulty, randomWord);
        }
        setIsLoading(false);
    };

    const handleRecordToggle = async () => {
        if (recordingState.isRecording) {
            const audioBlob = await stopRecording();
            if (audioBlob && gameState.currentWord) {
                await processAudio(audioBlob, gameState.currentWord.word);
            }
        } else {
            await startRecording();
        }
    };

    const handleNextWord = () => {
        if (availableWords.length > 0) {
            const randomWord = availableWords[Math.floor(Math.random() * availableWords.length)];
            setCurrentWord(randomWord);
            clearFeedback();
        }
    };

    if (isLoading) {
        return <GameScreenWrapper><LoadingSpinner size="lg" /></GameScreenWrapper>;
    }

    if (gameState.gameEnded) {
        return <GameEnded score={gameState.score} streak={gameState.streak} />;
    }

    if (!gameState.isPlaying) {
        return <GameSetup onStart={handleStartGame} />;
    }

    return (
        <GameScreenWrapper>
            <GameCard>
                <div className="flex justify-between items-center mb-6">
                    <div className="text-center">
                        <div className="text-3xl font-bold text-purple-600">{gameState.score}</div>
                        <div className="text-sm font-medium text-gray-600">Score</div>
                    </div>
                    <div className="text-center">
                        <div className="text-3xl font-bold text-blue-600">{gameState.streak}</div>
                        <div className="text-sm font-medium text-gray-600">Streak</div>
                    </div>
                </div>

                {gameState.currentWord && <WordDisplay word={gameState.currentWord} />}

                <div className="flex flex-col items-center space-y-6 mb-8">
                    {!feedback && <RecordButton isRecording={recordingState.isRecording} onToggle={handleRecordToggle} />}
                    {feedback && <FeedbackDisplay feedback={feedback} />}
                </div>

                <div className="flex flex-col sm:flex-row gap-4 w-full">
                    <Button
                        onClick={handleNextWord}
                        className="flex-1 h-14 text-lg font-bold bg-gradient-to-r from-green-500 to-emerald-500 shadow-lg"
                    >
                        Next Word <Send className="w-5 h-5 ml-2" />
                    </Button>
                    <Button
                        onClick={endGame}
                        variant="destructive"
                        className="flex-1 h-14 text-lg font-bold shadow-lg"
                    >
                        End Game <X className="w-5 h-5 ml-2" />
                    </Button>
                </div>
            </GameCard>
        </GameScreenWrapper>
    );
}